import { useState, type ChangeEventHandler } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { authInputBase } from "./authStyles";

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  /** Input classes; defaults to the shared auth input look. */
  className?: string;
  /** Wrapper classes — use to opt out of the default mt-1 field spacing. */
  wrapperClassName?: string;
}

/**
 * Password field with a show/hide toggle. Used on every surface that takes a
 * password (login, register, reset, join, change-password) so the toggle
 * behavior and a11y wiring stay identical.
 */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete = "current-password",
  placeholder,
  required,
  className,
  wrapperClassName = "relative mt-1",
}: PasswordInputProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <div className={wrapperClassName}>
      <input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        className={`${className ?? authInputBase} pr-10`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? t("auth.password.hide") : t("auth.password.show")}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
